import axios from "axios";
import api from "./client";
import type {
  EmployerDailyReport,
  EmployerInvite,
  EmployerInvitePublicInfo,
  EmployerProjectDetail,
  EmployerProjectSummary,
} from "../types";

/**
 * İşveren (owner mode) API client'ı.
 * - Davet yönetimi: ana auth client (ProjectPolicy::update yetkisi).
 * - Public davet + portal: taşeron kalıbındaki gibi ana client'tan BAĞIMSIZ
 *   mini axios; ability token AYRI `employer_token` anahtarında tutulur —
 *   normal auth token'ı (`token`) asla ezilmez.
 */

// ---- Davet yönetimi (auth'lu, ana client) ----

export async function fetchEmployerInvites(projectId: number): Promise<EmployerInvite[]> {
  const response = await api.get(`/api/v1/projects/${projectId}/employer-invites`);
  return response.data.data || [];
}

export async function createEmployerInvite(
  projectId: number,
  data: { email?: string | null; project_ids?: number[] },
): Promise<EmployerInvite> {
  const response = await api.post(`/api/v1/projects/${projectId}/employer-invites`, data);
  return response.data.data;
}

export async function revokeEmployerInvite(inviteId: number): Promise<EmployerInvite> {
  const response = await api.post(`/api/v1/employer-invites/${inviteId}/revoke`);
  return response.data.data;
}

export async function regenerateEmployerInvite(inviteId: number): Promise<EmployerInvite> {
  const response = await api.post(`/api/v1/employer-invites/${inviteId}/regenerate`);
  return response.data.data;
}

export function employerInviteLink(token: string): string {
  return `${window.location.origin}/santiye/employer/invite/${token}`;
}

// ---- Public + portal (mini axios, ayrı oturum) ----

const employerApi = axios.create({
  baseURL: "/santiye", // client.ts ile aynı base; dev'te Vite proxy çalışır
  timeout: 30000,
  withCredentials: false,
  headers: { Accept: "application/json" },
});

export const EMPLOYER_TOKEN_KEY = "employer_token";
export const EMPLOYER_USER_KEY = "employer_user";
export const EMPLOYER_INVITE_TOKEN_KEY = "employer_invite_token";

export interface EmployerUser {
  id: number;
  name: string;
  surname: string;
  email: string;
  role: string;
  is_external: boolean;
  external_type: string;
}

export function getEmployerToken(): string | null {
  return localStorage.getItem(EMPLOYER_TOKEN_KEY);
}

export function getEmployerUser(): EmployerUser | null {
  const raw = localStorage.getItem(EMPLOYER_USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as EmployerUser;
  } catch {
    return null;
  }
}

export function saveEmployerSession(token: string, user: EmployerUser, inviteToken: string): void {
  localStorage.setItem(EMPLOYER_TOKEN_KEY, token);
  localStorage.setItem(EMPLOYER_USER_KEY, JSON.stringify(user));
  localStorage.setItem(EMPLOYER_INVITE_TOKEN_KEY, inviteToken);
}

export function clearEmployerSession(): void {
  localStorage.removeItem(EMPLOYER_TOKEN_KEY);
  localStorage.removeItem(EMPLOYER_USER_KEY);
  localStorage.removeItem(EMPLOYER_INVITE_TOKEN_KEY);
}

function authHeaders(): Record<string, string> {
  const token = getEmployerToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// --- Public davet endpoint'leri (auth'siz) ---

export async function fetchEmployerInviteInfo(token: string): Promise<EmployerInvitePublicInfo> {
  const response = await employerApi.get(`/api/v1/employer/invite/${token}`);
  return response.data.data;
}

export async function acceptEmployerInvite(
  token: string,
  payload: { name: string; surname: string; email: string; password: string },
): Promise<{ token: string; user: EmployerUser; projects: { id: number; name: string }[] }> {
  const response = await employerApi.post(`/api/v1/employer/invite/${token}/accept`, payload);
  // 201 yanıtı { message, token, user, projects } — 'data' sarması yok.
  return response.data;
}

// --- Ability token korumalı portal endpoint'leri (EnsureEmployerScope) ---

export async function fetchEmployerProjects(): Promise<EmployerProjectSummary[]> {
  const response = await employerApi.get(`/api/v1/employer/projects`, {
    headers: authHeaders(),
  });
  return response.data.data || [];
}

export async function fetchEmployerProjectDetail(projectId: number): Promise<EmployerProjectDetail> {
  const response = await employerApi.get(`/api/v1/employer/projects/${projectId}`, {
    headers: authHeaders(),
  });
  return response.data.data;
}

export async function fetchEmployerDailyReports(
  projectId: number,
  page = 1,
): Promise<{ data: EmployerDailyReport[]; lastPage: number; total: number }> {
  const response = await employerApi.get(`/api/v1/employer/projects/${projectId}/daily-reports`, {
    headers: authHeaders(),
    params: { page },
  });
  return {
    data: response.data.data || [],
    lastPage: response.data.meta?.last_page ?? 1,
    total: response.data.meta?.total ?? 0,
  };
}

/** auth/logout whitelist'te; oturum zaten kapalıysa sessizce geç. */
export async function employerLogout(): Promise<void> {
  try {
    await employerApi.post(`/api/v1/auth/logout`, {}, { headers: authHeaders() });
  } catch {
    // Çıkış akışını engelleme.
  }
}
